import type {
  FetchError,
  SalonStockerHttpTypes,
  StoreAddSavedListToCartResponse,
  StoreSavedListDeleteResponse,
  StoreSavedListLineSelection,
  StoreSavedListResponse,
  StoreSavedListsResponse
} from "@repo/api-sdk";
import sdk from "@repo/api-sdk";
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient
} from "@tanstack/react-query";

import { invalidateSellerCarts } from "../cart/cache";
import { savedListsQueryKeys } from "./queryKeys";

/**
 * Client data layer for the buyer **Saved Lists** (F3): `queryOptions` for the
 * cheap index and per-list detail, a dry-run **preview** query for live
 * price/availability, and mutations for CRUD + cart interop.
 *
 * The `add-to-cart` mutation invalidates the seller-cart cache via the shared
 * `invalidateSellerCarts` owner - the same one the cart mutations use - so the
 * cart UI refreshes after a list fans out into the per-seller carts.
 */

// ============================================================
// Queries
// ============================================================

/** Cheap index of the salon's saved lists (counts only, no per-line resolve). */
export function savedListsQuery() {
  return queryOptions({
    queryKey: savedListsQueryKeys.list(),
    queryFn: () => sdk.store.savedList.list(),
    staleTime: 5 * 60 * 1000
  });
}

export function useSavedLists(options?: { enabled?: boolean }) {
  return useQuery({ ...savedListsQuery(), ...options });
}

/** A single saved list with its stored lines (no live price). */
export function savedListQuery(id: string) {
  return queryOptions({
    queryKey: savedListsQueryKeys.detail(id),
    queryFn: () => sdk.store.savedList.retrieve(id),
    staleTime: 5 * 60 * 1000
  });
}

export function useSavedList(id: string, options?: { enabled?: boolean }) {
  return useQuery({ ...savedListQuery(id), ...options });
}

/**
 * Live dry-run resolve of a list against current offers (price + availability
 * per line). Read-only server-side; a query so the detail view auto-loads it.
 * Kept fresher than the stored list since offers move.
 */
export function savedListPreviewQuery(id: string) {
  return queryOptions({
    queryKey: savedListsQueryKeys.preview(id),
    queryFn: () => sdk.store.savedList.preview(id),
    staleTime: 60 * 1000
  });
}

export function usePreviewSavedList(
  id: string,
  options?: { enabled?: boolean }
) {
  return useQuery({ ...savedListPreviewQuery(id), ...options });
}

// ============================================================
// Mutations
// ============================================================

export function useCreateSavedList() {
  const queryClient = useQueryClient();

  return useMutation<
    StoreSavedListResponse,
    FetchError,
    SalonStockerHttpTypes.StoreCreateSavedList
  >({
    mutationFn: (body) => sdk.store.savedList.create(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: savedListsQueryKeys.lists() });
    }
  });
}

export function useRenameSavedList() {
  const queryClient = useQueryClient();

  return useMutation<
    StoreSavedListResponse,
    FetchError,
    { listId: string; name: string }
  >({
    mutationFn: ({ listId, name }) => sdk.store.savedList.rename(listId, name),
    onSuccess: (_data, { listId }) => {
      queryClient.invalidateQueries({ queryKey: savedListsQueryKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: savedListsQueryKeys.detail(listId)
      });
    }
  });
}

export function useDeleteSavedList() {
  const queryClient = useQueryClient();

  return useMutation<StoreSavedListDeleteResponse, FetchError, string>({
    mutationFn: (listId) => sdk.store.savedList.delete(listId),
    onSuccess: (_data, listId) => {
      queryClient.removeQueries({
        queryKey: savedListsQueryKeys.detail(listId)
      });
      queryClient.invalidateQueries({ queryKey: savedListsQueryKeys.lists() });
    }
  });
}

/**
 * Invalidate everything a line edit affects: the list detail (rows), its live
 * preview (prices/availability), and the index (item/seller counts).
 */
function invalidateListAndPreview(
  queryClient: ReturnType<typeof useQueryClient>,
  listId: string
) {
  queryClient.invalidateQueries({
    queryKey: savedListsQueryKeys.detail(listId)
  });
  queryClient.invalidateQueries({
    queryKey: savedListsQueryKeys.preview(listId)
  });
  queryClient.invalidateQueries({ queryKey: savedListsQueryKeys.lists() });
}

export function useAddSavedListItem() {
  const queryClient = useQueryClient();

  return useMutation<
    StoreSavedListResponse,
    FetchError,
    { listId: string; item: SalonStockerHttpTypes.StoreAddSavedListItem }
  >({
    mutationFn: ({ listId, item }) => sdk.store.savedList.addItem(listId, item),
    onSuccess: (_data, { listId }) =>
      invalidateListAndPreview(queryClient, listId)
  });
}

/**
 * Add several lines to an existing list in one action (e.g. multiple variants
 * from a product page). Adds sequentially - there is no bulk route - and
 * invalidates once. Each add overwrites-on-conflict server-side, never sums.
 */
export function useAddSavedListItems() {
  const queryClient = useQueryClient();

  return useMutation<
    StoreSavedListResponse,
    FetchError,
    { listId: string; items: SalonStockerHttpTypes.StoreAddSavedListItem[] }
  >({
    mutationFn: async ({ listId, items }) => {
      let last: StoreSavedListResponse | undefined;
      for (const item of items) {
        last = await sdk.store.savedList.addItem(listId, item);
      }
      // `items` is always non-empty at the call site (guarded by the caller).
      return last as StoreSavedListResponse;
    },
    onSuccess: (_data, { listId }) =>
      invalidateListAndPreview(queryClient, listId)
  });
}

export function useUpdateSavedListItem() {
  const queryClient = useQueryClient();

  return useMutation<
    StoreSavedListResponse,
    FetchError,
    { listId: string; item: SalonStockerHttpTypes.StoreUpdateSavedListItem }
  >({
    mutationFn: ({ listId, item }) =>
      sdk.store.savedList.updateItem(listId, item),
    onSuccess: (_data, { listId }) =>
      invalidateListAndPreview(queryClient, listId)
  });
}

export function useRemoveSavedListItem() {
  const queryClient = useQueryClient();

  return useMutation<
    StoreSavedListResponse,
    FetchError,
    { listId: string; key: SalonStockerHttpTypes.StoreRemoveSavedListItem }
  >({
    mutationFn: ({ listId, key }) =>
      sdk.store.savedList.removeItem(listId, key),
    onSuccess: (_data, { listId }) =>
      invalidateListAndPreview(queryClient, listId)
  });
}

export interface AddListToCartInput {
  listId: string;
  /** Buyer-picked subset + quantities; omit to add every addable line. */
  selections?: StoreSavedListLineSelection[];
}

/**
 * Fan a list out into the per-seller carts - or, when `selections` is given,
 * just the buyer's picked subset at their picked quantities. Invalidates the
 * seller carts (new lines) and the index (bumped `last_used_at`). The `carts`
 * in the response are the affected seller carts.
 */
export function useAddListToCart() {
  const queryClient = useQueryClient();

  return useMutation<
    StoreAddSavedListToCartResponse,
    FetchError,
    AddListToCartInput
  >({
    mutationFn: ({ listId, selections }) =>
      sdk.store.savedList.addToCart(listId, selections),
    onSettled: () => {
      invalidateSellerCarts(queryClient);
      queryClient.invalidateQueries({ queryKey: savedListsQueryKeys.lists() });
    }
  });
}

/**
 * Snapshot the active seller carts into a new list (non-destructive). Only the
 * index changes - the carts are untouched.
 */
export function useSaveCartAsList() {
  const queryClient = useQueryClient();

  return useMutation<StoreSavedListResponse, FetchError, { name?: string }>({
    mutationFn: ({ name } = {}) => sdk.store.savedList.fromCart(name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: savedListsQueryKeys.lists() });
    }
  });
}
